import { useCallback, useEffect, useState, ChangeEvent } from 'react';
import { Box, Tooltip, Typography } from '@material-ui/core';
import Head from '../../../components/Head';
import Layout from '../../../layout';
import TableDragAndDrop from './TableDragAndDrop';
import OrderReceived from '../OrderReceived';

import { FleetsService } from '../../../services';
import { snackbarUtils } from '../../../utils';

import useStyles from './styles';
import { AuthService } from '../../../services';
import { useCars } from '../hooks/useCars';
import Modal from '../../../components/Fleets/Modal';
import OrderCanceled from '../OrderCanceled';
import OrderChanged from '../OrderChanged';

const orderErrorMessage = (error: any) =>
  error?.response?.data?.message === 'out-of-period'
    ? 'A ação está fora do período de participação.'
    : 'Não foi possível concluir. Tente novamente.';

const termsService = `${(process.env.REACT_APP_API_ENDPOINT || '').replace(
  /\/$/,
  ''
)}/public/leilao-2026/documentos/termos-e-condicoes-venda-veiculos-2026.pdf`;

type EndDate = {
  endDate: string;
};

type Period = {
  startDate: string;
  endDate: string;
  open: boolean;
};

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });

const CarsSelected = () => {
  const classes = useStyles();
  const { carSelected, hasOrder, setHasOrder, setCarSelected } = useCars();
  const [checked, setChecked] = useState(false);
  const [submitOrder, setSubmitOrder] = useState(false);
  const [cancelOrder, setCancelOrder] = useState(false);
  const [updateOrder, setUpdateOrder] = useState(false);
  const [endDateSolicitation, setEndDateSolicitation] = useState<EndDate>(
    {} as EndDate
  );

  const [period, setPeriod] = useState<Period | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [buttonSubmit, setButtonSubmit] = useState<string>(() => {
    return classes.buttonTermsDisabled;
  });

  useEffect(() => {
    const [user] = AuthService.getUserFromToken();
    setUserName((user as any)?.firstName as string);
    (async () => {
      const { data: periodData } = await FleetsService.period();
      setPeriod(periodData);
      const { data } = await FleetsService.endDate();
      const order = await FleetsService.listOrder();
      setEndDateSolicitation((prevState) => {
        const formatedDate = {
          ...prevState,
          endDate: new Date((data || periodData)?.endDate).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: 'UTC',
          }),
        };
        setHasOrder(!!order.data.length);
        return formatedDate;
      });
    })();
  }, [setHasOrder]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setChecked((prevState) => !prevState);
      if (checked) {
        setButtonSubmit(classes.buttonTermsDisabled);
      } else {
        setButtonSubmit(classes.buttonTerms);
      }
    },
    [classes, checked]
  );

  const handleSubmitCarsSelected = useCallback(async () => {
    if (!checked) return;
    const vehicles = carSelected.map((car, index) => ({
      vehicleId: car.id,
      position: index + 1,
    }));

    const order = {
      vehicles,
    };
    try {
      await FleetsService.createOrder(order);
      setSubmitOrder(true);
    } catch (error: any) {
      snackbarUtils.error(orderErrorMessage(error));
    }
  }, [checked, carSelected]);

  const handleEditOrder = useCallback(async () => {
    if (!checked) return;
    const orderId = (carSelected[0] as any).orderId;
    const vehicles = carSelected.map((car, index) => ({
      vehicleId: car.id,
      position: index + 1,
    }));

    const order = {
      vehicles,
    };
    try {
      await FleetsService.updateOrder(order, orderId);
      setUpdateOrder(true);
    } catch (error: any) {
      snackbarUtils.error(orderErrorMessage(error));
    }
  }, [carSelected, checked]);

  const handleCancelOrder = useCallback(async () => {
    try {
      await FleetsService.cancelOrder((carSelected[0] as any).orderId);
      setCarSelected([]);
    } catch (error: any) {
      snackbarUtils.error(orderErrorMessage(error));
    }
  }, [carSelected, setCarSelected]);

  const handleModalClose = () => {};

  const handleModalCancel = () => {
    handleCancelOrder();
    setModalOpen(false);
    setCancelOrder(true);
  };

  const handleModalBack = () => {
    setModalOpen(false);
  };

  const openModal = () => {
    setModalOpen(true);
  };

  const renderPage = () => {
    if (updateOrder) {
      return <OrderChanged />;
    }
    if (cancelOrder) {
      return <OrderCanceled />;
    }
    if (submitOrder) {
      return <OrderReceived />;
    }

  const tooltipTitle = carSelected.length > 3 ? "Você pode selecionar até 3 veículos" : "Você deve selecionar pelo menos 1 veículo";

    return (
      <Box component="main" className={classes.container}>
        {!carSelected.length ? (
          <Box>
            <Typography className={classes.title}>
              Você não possui nenhum carro selecionado
            </Typography>
          </Box>
        ) : (
          <>
            <Box component="section" className={classes.wrapperContent}>
              <Box mb={2}>
                {!hasOrder ? (
                  <Typography className={classes.title}>
                    Veículos que tenho interesse
                  </Typography>
                ) : null}
                <Box pl={1}>
                  <Typography variant="caption">
                    Você pode demonstrar interesse{' '}
                    <strong>em até 3 veículos.</strong>
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.contentContainer}>
                <TableDragAndDrop />
              </Box>
            </Box>

            {!hasOrder ? (
              <Box className={classes.warning}>
                <Typography className={classes.warningTitle}>
                  Aviso importante
                </Typography>
                <p className={classes.warningText}>
                  Você estará em disputa para compra deste veículo, com outros
                  funcionários da PepsiCo. A{' '}
                  <strong>ordem de prioridade definida</strong>, indicará qual
                  veículo você tem maior preferência pela compra.
                </p>
              </Box>
            ) : (
              <Box className={classes.warning}>
                <Typography className={classes.warningTitle}>
                  Importante
                </Typography>
                <p className={classes.warningText}>
                  Você pode alterar a{' '}
                  <strong>
                    ordem de prioridade, remover ou adicionar veículos
                  </strong>{' '}
                  à sua solicitação até o dia{' '}
                  <strong>{endDateSolicitation.endDate}.</strong>
                </p>
              </Box>
            )}
            {period && !period.open ? (
              <Box className={classes.warning}>
                <Typography className={classes.warningTitle}>
                  Fora do período de participação
                </Typography>
                <p className={classes.warningText}>
                  A inscrição vai de <strong>{formatDateTime(period.startDate)}</strong>{' '}
                  até <strong>{formatDateTime(period.endDate)}</strong> (horário de
                  Brasília). Fora desse período não é possível enviar, alterar ou
                  cancelar a solicitação.
                </p>
              </Box>
            ) : hasOrder ? (
              <Box className={classes.buttonsSubmit}>
                <input
                  type="checkbox"
                  className={classes.check}
                  onChange={handleChange}
                />
                <button
                  className={buttonSubmit}
                  onClick={handleEditOrder}
                  disabled={!checked}
                >
                  Editar
                </button>
                <button className={classes.buttonTerms} onClick={openModal}>
                  Cancelar Compra
                </button>
              </Box>
            ) : (
              <Box className={classes.termsWrapper}>
                <input
                  type="checkbox"
                  className={classes.check}
                  onChange={handleChange}
                />

                <Box display="flex" flexDirection="column">
                  <p className={classes.termsText}>
                    Eu {`${userName}`}, afirmo que li e estou de acordo com
                    <br /> os{' '}
                    <u>
                      <a href={termsService} target="_blank" rel="noreferrer">
                        termos de solicitação
                      </a>
                    </u>{' '}
                    de compra de veículos da frota PepsiCo.
                  </p>
                  <Tooltip title={tooltipTitle}>
                    <button
                      className={buttonSubmit}
                      onClick={handleSubmitCarsSelected}
                      disabled={
                        !checked ||
                        carSelected.length > 3 ||
                        carSelected.length < 1
                      }
                    >
                      Concluir Pedido
                    </button>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <Layout page="fleetsCarSelected">
      <Modal
        open={modalOpen}
        handleClose={handleModalClose}
        handleBack={handleModalBack}
        handlecancel={handleModalCancel}
      />
      <Head>Veículos selecionados</Head>
      {renderPage()}
    </Layout>
  );
};

export default CarsSelected;
